import { useIsCoApplicantEnabled } from "@lending/hooks/useIsCoApplicantEnabled";
import { useIsPlaidLayerEnabled } from "@lending/hooks/useIsPlaidLayerEnabled";
import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";
import { useLendingApplicationSteps } from "@lending/hooks/useLendingApplicationSteps";
import { useAutoLoanFlowState } from "@lending/navigation/hooks/useAutoLoanFlowState";
import { usePrefilledOlbSteps } from "@lending/navigation/hooks/usePrefilledOlbSteps";
import { useStore } from "../../contexts/StoreContext";
import {
  type NavigationContext,
  flowConfig,
  getCurrentFlowStepId,
  getFlowSteps,
} from "../flowConfig";
import type { FlowType, ProductType } from "../types";

function shouldAssumeExpandedCoApplicantProgress(
  currentStepId: string | null,
  productType: ProductType,
  flowType: FlowType
): boolean {
  if (!currentStepId) {
    return false;
  }

  if (currentStepId.startsWith("co-applicant-")) {
    return true;
  }

  const steps = flowConfig[productType][flowType];
  const currentStepIndex = steps.findIndex((step) => step.id === currentStepId);
  const coApplicantAskIndex = steps.findIndex((step) => step.id === "co-applicant-ask");

  if (currentStepIndex === -1 || coApplicantAskIndex === -1) {
    return false;
  }

  return currentStepIndex < coApplicantAskIndex;
}

/**
 * Hook to get the progress percentage for the current path in a flow.
 * Pulls context from the store automatically.
 */
export function useFlowProgressPercentage(
  currentPath: string,
  productType: ProductType | null,
  flowType: FlowType
): number {
  const isCoApplicantEnabled = useIsCoApplicantEnabled();
  const isPlaidLayerEnabled = useIsPlaidLayerEnabled();
  const { showLivingArrangementStep, showEmploymentDetailsStep } = useLendingApplicationSteps();
  const applicationUuid = useLendingApplicationID();
  const { data: storeData } = useStore();
  const prefilledOlbSteps = usePrefilledOlbSteps();
  const autoLoanFlowState = useAutoLoanFlowState(productType === "auto-loan");

  if (!productType) {
    return 0;
  }

  const currentStepId = getCurrentFlowStepId(currentPath, productType, flowType, applicationUuid);
  const shouldAssumeCoApplicant =
    isCoApplicantEnabled &&
    (storeData.hasCoApplicant === true ||
      shouldAssumeExpandedCoApplicantProgress(currentStepId, productType, flowType));
  const isSeparateCoApplicantAddressPath = currentPath.endsWith("/co-applicant/address");
  const hasCoApplicant = shouldAssumeCoApplicant ? true : (storeData.hasCoApplicant ?? undefined);
  const hasSeparateCoApplicantAddress =
    storeData.hasSeparateCoApplicantAddress ??
    (isSeparateCoApplicantAddressPath ? true : undefined);
  const hasVehicleSelectedForProgress =
    productType === "auto-loan"
      ? (autoLoanFlowState.hasVehicleSelected ?? true)
      : (storeData.hasVehicleSelected ?? undefined);
  const isRefinanceForProgress =
    productType === "auto-loan"
      ? (autoLoanFlowState.isRefinance ?? storeData.isRefinanceSelected ?? undefined)
      : undefined;
  const isAutoRequestedAmountEnabled =
    productType === "auto-loan" ? autoLoanFlowState.isAutoRequestedAmountEnabled : undefined;

  const context: NavigationContext = {
    isPlaidLayerEnabled: isPlaidLayerEnabled,
    skippedPlaidPrefill: storeData.skippedPlaidPrefill ?? undefined,
    applicationState: storeData.applicationState ?? undefined,
    isCoApplicantEnabled,
    showLivingArrangementStep,
    showEmploymentDetailsStep,
    // The OLB flow skips prefilled steps, so progress has to filter on the same flags
    // navigation does or the bar counts pages the applicant never sees.
    hasPrefilledLivingArrangement: prefilledOlbSteps.hasPrefilledLivingArrangement,
    hasPrefilledIncome: prefilledOlbSteps.hasPrefilledIncome,
    hasPrefilledEmployment: prefilledOlbSteps.hasPrefilledEmployment,
    hasCoApplicant,
    hasSeparateCoApplicantAddress,
    hasMultipleCreditCardProducts: storeData.hasMultipleCreditCardProducts ?? undefined,
    hasSelectedCreditCardProduct: storeData.hasSelectedCreditCardProduct ?? undefined,
    hasVehicleSelected: hasVehicleSelectedForProgress,
    isRefinance: isRefinanceForProgress,
    isAutoRequestedAmountEnabled,
    hasEligibilityCriteria: storeData.hasEligibilityCriteria ?? undefined,
    hasLendingQuestions: storeData.hasLendingQuestions ?? undefined,
    incomeType: storeData.incomeType ?? undefined,
    coApplicantIncomeType: storeData.coApplicantIncomeType ?? undefined,
  };

  const steps = getFlowSteps(productType, flowType, context, applicationUuid);

  if (steps.length === 0) {
    return 0;
  }

  const currentIndex = steps.findIndex((step) => step.id === currentStepId);

  if (currentIndex === -1) {
    return 0;
  }

  return Math.round(((currentIndex + 1) / steps.length) * 100);
}
